import { Pencil } from 'lucide-react';
import { useState } from 'react';
import { Badge } from '@/ui/components/ui/badge';
import { Button } from '@/ui/components/ui/button';
import { Card, CardContent } from '@/ui/components/ui/card';
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
} from '@/ui/components/ui/dialog';
import { Input } from '@/ui/components/ui/input';
import { Label } from '@/ui/components/ui/label';
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/ui/components/ui/select';
import { Textarea } from '@/ui/components/ui/textarea';
import type { ProjectObject } from '../../hooks/use-admin';
import { VOTING_MODES } from '../../hooks/use-admin';
import { formatEnum } from '../../utils/format-enum';

interface ProjectHeaderProps {
	project: ProjectObject;
	updateProject: (opts: {
		variables: { id: string; input: Partial<ProjectObject> };
	}) => Promise<unknown>;
	canEdit: boolean;
}

export function ProjectHeader({
	project,
	updateProject,
	canEdit,
}: ProjectHeaderProps) {
	const [open, setOpen] = useState(false);
	const [editForm, setEditForm] = useState<Partial<ProjectObject>>({});
	const [editError, setEditError] = useState<string | null>(null);
	const [editSubmitting, setEditSubmitting] = useState(false);

	const openDialog = () => {
		setEditForm({
			name: project.name,
			description: project.description ?? '',
			votingMode: project.votingMode.toUpperCase(),
			status: project.status,
		});
		setEditError(null);
		setOpen(true);
	};

	const handleSave = async (e: React.FormEvent) => {
		e.preventDefault();
		setEditError(null);
		setEditSubmitting(true);
		try {
			await updateProject({
				variables: {
					id: project.id,
					input: {
						name: editForm.name,
						description: editForm.description || undefined,
						votingMode: editForm.votingMode as
							| 'TRACKED'
							| 'OPEN'
							| 'COOKIE'
							| undefined,
						status: editForm.status,
					},
				},
			});
			setOpen(false);
		} catch (err) {
			setEditError(
				err instanceof Error ? err.message : 'Failed to update project',
			);
		} finally {
			setEditSubmitting(false);
		}
	};

	return (
		<>
			<Card className='mb-6'>
				<CardContent className='px-5 py-0'>
					<div className='flex items-start justify-between gap-3'>
						<div className='min-w-0 flex-1'>
							<h1 className='text-base font-semibold text-foreground leading-tight'>
								{project.name}
							</h1>
							{project.description && (
								<p className='text-sm text-muted-foreground mt-0.5'>
									{project.description}
								</p>
							)}
							<div className='flex items-center gap-4 mt-3 text-xs text-muted-foreground'>
								<span className='flex items-center gap-1.5'>
									<span className='font-medium text-foreground/70'>
										Tenant
									</span>
									{project.tenantName ?? project.tenantId}
								</span>
								<span className='w-px h-3 bg-border' />
								<span className='flex items-center gap-1.5'>
									<span className='font-medium text-foreground/70'>
										Status
									</span>
									<Badge
										variant={
											project.status === 'active'
												? 'default'
												: 'secondary'
										}
										className='text-[11px] px-1.5 py-0'
									>
										{formatEnum(project.status)}
									</Badge>
								</span>
								<span className='w-px h-3 bg-border' />
								<span className='flex items-center gap-1.5'>
									<span className='font-medium text-foreground/70'>
										Voting
									</span>
									<Badge
										variant='outline'
										className='text-[11px] px-1.5 py-0'
									>
										{formatEnum(project.votingMode)}
									</Badge>
								</span>
							</div>
						</div>
						{canEdit && (
							<Button
								variant='ghost'
								size='icon'
								className='h-7 w-7 shrink-0 text-muted-foreground'
								onClick={openDialog}
							>
								<Pencil className='size-3.5' />
							</Button>
						)}
					</div>
				</CardContent>
			</Card>

			<Dialog open={open} onOpenChange={setOpen}>
				<DialogContent className='sm:max-w-md'>
					<DialogHeader>
						<DialogTitle>Edit project</DialogTitle>
					</DialogHeader>
					<form onSubmit={handleSave} className='flex flex-col gap-4'>
						<div className='flex flex-col gap-1.5'>
							<Label htmlFor='edit-name'>Name</Label>
							<Input
								id='edit-name'
								required
								value={editForm.name ?? ''}
								onChange={(e) =>
									setEditForm((f) => ({
										...f,
										name: e.target.value,
									}))
								}
							/>
						</div>
						<div className='flex flex-col gap-1.5'>
							<Label htmlFor='edit-desc'>Description</Label>
							<Textarea
								id='edit-desc'
								rows={3}
								value={editForm.description ?? ''}
								onChange={(e) =>
									setEditForm((f) => ({
										...f,
										description: e.target.value,
									}))
								}
							/>
						</div>
						<div className='grid grid-cols-2 gap-3'>
							<div className='flex flex-col gap-1.5'>
								<Label htmlFor='edit-voting'>Voting mode</Label>
								<Select
									value={editForm.votingMode ?? ''}
									onValueChange={(v) =>
										setEditForm((f) => ({
											...f,
											votingMode: v,
										}))
									}
								>
									<SelectTrigger
										id='edit-voting'
										className='w-full'
									>
										<SelectValue />
									</SelectTrigger>
									<SelectContent>
										{VOTING_MODES.map((m) => (
											<SelectItem key={m} value={m}>
												{formatEnum(m)}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							</div>
							<div className='flex flex-col gap-1.5'>
								<Label htmlFor='edit-status'>Status</Label>
								<Select
									value={editForm.status ?? ''}
									onValueChange={(v) =>
										setEditForm((f) => ({
											...f,
											status: v,
										}))
									}
								>
									<SelectTrigger
										id='edit-status'
										className='w-full'
									>
										<SelectValue />
									</SelectTrigger>
									<SelectContent>
										<SelectItem value='active'>
											Active
										</SelectItem>
										<SelectItem value='inactive'>
											Inactive
										</SelectItem>
									</SelectContent>
								</Select>
							</div>
						</div>
						{editError && (
							<p className='text-sm text-destructive rounded-lg bg-destructive/10 px-3 py-2'>
								{editError}
							</p>
						)}
						<div className='flex gap-2 justify-end'>
							<Button
								type='button'
								variant='outline'
								size='sm'
								onClick={() => setOpen(false)}
							>
								Cancel
							</Button>
							<Button
								type='submit'
								size='sm'
								disabled={editSubmitting}
							>
								{editSubmitting ? 'Saving…' : 'Save changes'}
							</Button>
						</div>
					</form>
				</DialogContent>
			</Dialog>
		</>
	);
}
